import type { QuotePiece } from '../../../apis/fedexQuote';
import type { SelectedProduct } from '../components/ProductMultiSelectField';
import {
  productsPayloadFromOutboundLines,
  productsPayloadFromSelectedRows,
  weightLbToKg,
} from '../shellShipmentHelpers';

export const ALL_PRODUCTS = 'all_products';
const IN_TO_CM = 2.54;

/** Serial inputs hold one or more serials separated by commas. */
const serialTokens = (serial?: string): string[] =>
  (serial ?? '')
    .split(',')
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);

export const serialBoxes = (
  lists: SelectedProduct[][]
): Map<string, Set<number>> => {
  const boxesBySerial = new Map<string, Set<number>>();
  lists.forEach((list, box) => {
    list.forEach((p) => {
      serialTokens(p.serial_number).forEach((token) => {
        const boxes = boxesBySerial.get(token) ?? new Set<number>();
        boxes.add(box);
        boxesBySerial.set(token, boxes);
      });
    });
  });
  return boxesBySerial;
};

/** Serials entered in two different boxes (case and spacing ignored). */
export const hasCrossBoxDuplicateSerial = (
  lists: SelectedProduct[][]
): boolean =>
  Array.from(serialBoxes(lists).values()).some((boxes) => boxes.size > 1);

export const serialWarning = (
  lists: SelectedProduct[][],
  box: number,
  product: SelectedProduct,
  boxesBySerial: Map<string, Set<number>> = serialBoxes(lists)
): string | undefined => {
  const clashes = serialTokens(product.serial_number).flatMap((token) =>
    Array.from(boxesBySerial.get(token) ?? []).filter((b) => b !== box)
  );
  if (clashes.length === 0) return undefined;
  const boxes = Array.from(new Set(clashes))
    .sort((a, b) => a - b)
    .map((b) => b + 1)
    .join(', ');
  return `A serial number is already used in box ${boxes}. Each serial can be in one box only.`;
};

interface ProductTotalDiff {
  id: string;
  assigned: number;
  outbound: number;
}

const productTotals = (lists: SelectedProduct[][]): Record<string, number> => {
  const totals: Record<string, number> = {};
  lists.flat().forEach((p) => {
    if (p.id === ALL_PRODUCTS) return;
    totals[p.id] = (totals[p.id] ?? 0) + Math.max(1, Number(p.quantity) || 1);
  });
  return totals;
};

/** Outbound totals are always kept for the diff notice; the selection is prefilled only before the user edits a box. */
export const outboundPrefill = (
  rows: SelectedProduct[],
  touched: boolean
): { totals: Record<string, number>; selection: SelectedProduct[] | null } => ({
  totals: productTotals([rows]),
  selection: touched
    ? null
    : rows.map(({ id, quantity, serial_number }) => ({
        id,
        quantity,
        ...(serial_number ? { serial_number } : {}),
      })),
});

/** Products whose assigned total differs from the outbound shipment's. */
export const diffAgainstOutbound = (
  outbound: Record<string, number>,
  lists: SelectedProduct[][]
): ProductTotalDiff[] => {
  const assigned = productTotals(lists);
  return Array.from(
    new Set([...Object.keys(outbound), ...Object.keys(assigned)])
  )
    .map((id) => ({
      id,
      assigned: assigned[id] ?? 0,
      outbound: outbound[id] ?? 0,
    }))
    .filter((d) => d.assigned !== d.outbound);
};

/** One summed row per product, for the flat `products` field of the shipment. */
export const summedProductsPayload = (lists: SelectedProduct[][]) =>
  productsPayloadFromOutboundLines(
    lists.flat().flatMap((p) =>
      p.id === ALL_PRODUCTS
        ? []
        : [
            {
              product_id: p.id,
              quantity: p.quantity,
              serial_number: p.serial_number,
            },
          ]
    )
  );

interface ShipPiece {
  sequence: number;
  trackingNumber?: string | null;
}

const toCm = (inches?: number): number | undefined =>
  inches ? Math.round(inches * IN_TO_CM * 10) / 10 : undefined;

/**
 * `pieces[]` for create: carrier tracking numbers joined to each box's weight, size and products.
 * Undefined when any box has no tracking number, so the caller books flat products instead.
 */
export const buildCreatePieces = (
  quotePieces: QuotePiece[],
  shipPieces: ShipPiece[],
  lists: SelectedProduct[][]
) => {
  const trackingNumbers = quotePieces.map(
    (_, i) => shipPieces.find((p) => p.sequence === i + 1)?.trackingNumber
  );
  if (trackingNumbers.some((t) => !t)) return undefined;
  return quotePieces.map((piece, i) => {
    const products = productsPayloadFromSelectedRows(lists[i] ?? []);
    const length = toCm(piece.length);
    const width = toCm(piece.width);
    const height = toCm(piece.height);
    return {
      sequence: i + 1,
      tracking_number: trackingNumbers[i] as string,
      weight_kg: weightLbToKg(piece.weight),
      ...(length && width && height
        ? { length_cm: length, width_cm: width, height_cm: height }
        : {}),
      ...(piece.reference ? { reference: piece.reference } : {}),
      ...(products.length ? { products } : {}),
    };
  });
};
